/**
 * useCalendarTables - the Manpower calendar's tables, read from and written to
 * /api/manpower/tables (routes/manpowerTables.js, 10/16/26).
 *
 * Each change shows at once and is sent straight away; when the server says
 * no, the server's copy is read back and `error` says why (a table that still
 * has people in it, a name already taken ...). Someone else's change (a live
 * 'manpower' event) re-reads quietly: a re-read that fails keeps what is on
 * screen. Until the first read lands the layout is today's default, so the
 * calendar draws as it always has.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_LAYOUT, layoutOf, orderedTables } from './calendarTables';
import { latestReads, useLiveRefresh } from '../../live/useLive';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

async function send(url, method, body) {
  const r = await fetch(url, { method, headers: JSON_HEADERS, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  let data = null;
  try {
    data = await r.json();
  } catch {
    /* no body */
  }
  if (!r.ok) throw new Error((data && data.error) || `Could not save (HTTP ${r.status}).`);
  return data;
}

export function useCalendarTables() {
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const [reads] = useState(latestReads);

  const read = useCallback(async () => {
    const t = reads.start();
    try {
      const r = await fetch('/api/manpower/tables');
      if (!r.ok) return;
      const d = await r.json();
      if (alive.current && reads.show(t)) {
        setLayout(layoutOf(d));
        setLoaded(true);
      }
    } catch {
      /* keep what is on screen */
    }
  }, [reads]);

  useEffect(() => {
    alive.current = true;
    read();
    return () => {
      alive.current = false;
    };
  }, [read]);
  useLiveRefresh('manpower', read);

  // Show the change, send it, and on a refusal say why and read the real copy back.
  const run = useCallback(
    async (optimistic, request) => {
      setError(null);
      if (optimistic) setLayout(optimistic);
      try {
        const out = await request();
        return out;
      } catch (e) {
        if (alive.current) setError(e.message);
        await read();
        return null;
      }
    },
    [read]
  );

  const addTable = useCallback(
    async (name) => {
      const out = await run(null, () => send('/api/manpower/tables', 'POST', { name }));
      if (out) await read();
      return out;
    },
    [run, read]
  );

  const renameTable = useCallback(
    (id, name) =>
      run({ ...layout, tables: layout.tables.map((t) => (t.id === id ? { ...t, name } : t)) }, () =>
        send(`/api/manpower/tables/${encodeURIComponent(id)}`, 'PATCH', { name })
      ),
    [layout, run]
  );

  const setCounts = useCallback(
    (id, countsUtilization) =>
      run({ ...layout, tables: layout.tables.map((t) => (t.id === id ? { ...t, countsUtilization } : t)) }, () =>
        send(`/api/manpower/tables/${encodeURIComponent(id)}`, 'PATCH', { countsUtilization })
      ),
    [layout, run]
  );

  /** Move a table one place up (-1) or down (+1). */
  const shiftTable = useCallback(
    (id, dir) => {
      const ids = orderedTables(layout).map((t) => t.id);
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return null;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      return run({ ...layout, tables: layout.tables.map((t) => ({ ...t, position: ids.indexOf(t.id) })) }, () =>
        send('/api/manpower/tables/order', 'PUT', { ids })
      );
    },
    [layout, run]
  );

  const deleteTable = useCallback(
    (id) =>
      run({ ...layout, tables: layout.tables.filter((t) => t.id !== id) }, () =>
        send(`/api/manpower/tables/${encodeURIComponent(id)}`, 'DELETE')
      ),
    [layout, run]
  );

  const moveEngineer = useCallback(
    (engineer, tableId) => {
      if (!engineer || engineer.id == null) return null;
      return run({ ...layout, members: { ...layout.members, [engineer.id]: tableId } }, () =>
        send(`/api/manpower/members/${encodeURIComponent(engineer.id)}`, 'PUT', { tableId })
      );
    },
    [layout, run]
  );

  return {
    layout,
    loaded,
    error,
    clearError: () => setError(null),
    addTable,
    renameTable,
    setCounts,
    shiftTable,
    deleteTable,
    moveEngineer,
  };
}
